// Apolo IDE — SubagentTracker (Sprint v4 Epic S3 WS S3.4).
//
// Lists every subagent_run under a dispatch with a status badge, model,
// goal, tokens, and cost. Renders nothing when the runs list is empty
// (orchestrator hasn't fired yet OR dispatch had zero subagents).

import { CheckCircle, Loader2, XCircle, Ban, Clock, AlertTriangle } from 'lucide-react';

function statusBadgeClass(status) {
  switch (status) {
    case 'completed': return 'bg-apolo-success/15 text-apolo-success';
    case 'failed':
    case 'failed_timeout': return 'bg-apolo-error/15 text-apolo-error';
    case 'cancelled': return 'bg-apolo-warning/15 text-apolo-warning';
    case 'running': return 'bg-apolo-accent/15 text-apolo-accent';
    case 'pending': return 'bg-apolo-surface2 text-apolo-textTertiary';
    default: return 'bg-apolo-surface2 text-apolo-textTertiary';
  }
}

function StatusIcon({ status }) {
  if (status === 'running') return <Loader2 size={10} className="animate-spin" aria-hidden="true" />;
  if (status === 'completed') return <CheckCircle size={10} aria-hidden="true" />;
  if (status === 'failed' || status === 'failed_timeout')
    return <XCircle size={10} aria-hidden="true" />;
  if (status === 'cancelled') return <Ban size={10} aria-hidden="true" />;
  if (status === 'pending') return <Clock size={10} aria-hidden="true" />;
  return <AlertTriangle size={10} aria-hidden="true" />;
}

function fmtUsd(c) {
  if (typeof c !== 'number' || !Number.isFinite(c) || c <= 0) return '$0';
  if (c < 0.001) return '< $0.001';
  return `$${c.toFixed(c < 0.01 ? 4 : 3)}`;
}

function fmtTokens(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '0';
  if (n < 1000) return String(n);
  return `${(n / 1000).toFixed(1)}k`;
}

function goalOf(run) {
  if (run.spec_json && typeof run.spec_json === 'object' && run.spec_json.goal) {
    return run.spec_json.goal;
  }
  return '(no goal)';
}

function modelOf(run) {
  if (run.spec_json && typeof run.spec_json === 'object' && run.spec_json.model) {
    return run.spec_json.model;
  }
  return 'unknown';
}

export default function SubagentTracker({ runs }) {
  const list = Array.isArray(runs) ? runs : [];
  if (list.length === 0) {
    return (
      <div
        data-testid="subagent-tracker-empty"
        className="rounded-md border border-dashed border-apolo-borderSubtle bg-apolo-surface1 px-3 py-2 text-center text-[11px] text-apolo-textTertiary"
      >
        Nenhum subagent ativo.
      </div>
    );
  }

  return (
    <section
      data-testid="subagent-tracker"
      aria-label="Lista de subagents"
      className="space-y-1"
    >
      {list.map((r, idx) => (
        <article
          key={r.id || idx}
          data-testid="subagent-row"
          data-status={r.status}
          className="flex items-center justify-between gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface1 px-3 py-1.5 text-[11px]"
        >
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 ${statusBadgeClass(r.status)}`}
              data-testid="subagent-status-badge"
            >
              <StatusIcon status={r.status} />
              {r.status}
            </span>
            <span className="font-mono text-[10px] text-apolo-textTertiary">#{idx + 1}</span>
            <span className="truncate text-apolo-textPrimary" title={goalOf(r)}>{goalOf(r)}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2 text-apolo-textTertiary">
            <span className="font-mono">{modelOf(r)}</span>
            <span className="font-mono" data-testid="subagent-tokens">{fmtTokens(r.tokens_used || 0)}</span>
            <span className="font-mono" data-testid="subagent-cost">{fmtUsd(r.cost_usd || 0)}</span>
          </div>
        </article>
      ))}
    </section>
  );
}
